import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentCompanionIntroGetInput,
  type EnrollmentCompanionIntroData,
  type EnrollmentCompanionIntroSubmitInput,
  type EnrollmentCompanionIntroSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment CompanionIntro data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentCompanionIntroData>
 * @example const response = await getEnrollmentCompanionIntroData()
 */
export const getEnrollmentCompanionIntroData = async (
  enrollmentCompanionIntroSubmitInput?: EnrollmentCompanionIntroGetInput,
  context?: DefaultContext,
): Promise<EnrollmentCompanionIntroData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentCompanionIntroData',
      query: `mutation getEnrollmentCompanionIntroData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              experimentId
              investmentProfile {
                account {
                  includeCompanionAccount
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentCompanionIntroSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentCompanionIntroData }
  return response.submitEnrollment
}

export const useGetEnrollmentCompanionIntroData = createQueryHook<
  EnrollmentCompanionIntroGetInput,
  EnrollmentCompanionIntroData
>(getEnrollmentCompanionIntroData)

/**
 * Submit Enrollment CompanionIntro data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentCompanionIntroSubmitOutput>
 * @example const response = await submitEnrollmentCompanionIntroData();
 */
export const submitEnrollmentCompanionIntroData = async (
  enrollmentCompanionIntroSubmitInput?: EnrollmentCompanionIntroSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentCompanionIntroSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentCompanionIntroData',
      query: `mutation submitEnrollmentCompanionIntroData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
        ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentCompanionIntroSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentCompanionIntroSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentCompanionIntroData = createMutationHook<
  EnrollmentCompanionIntroSubmitInput,
  EnrollmentCompanionIntroSubmitOutput
>(submitEnrollmentCompanionIntroData)
